// Costs-by-context (Nav Phase 2, triage dashboard): a deterministic breakdown
// of the case's verified amounts grouped by the four types — Cost incurred,
// Recovery / received, Coverage term, Rate. Every number here comes from the
// shipped totaling module (computeCaseTotals): hard-verified facts only,
// fold-subsumption + exclude disposition + override-wins all already applied.
// No LLM in the computation; an LLM may caption the FINISHED table afterward,
// reading these numbers verbatim, never altering them.
//
// Single source of truth: each group's subtotal is bucketSubtotalCents — the
// exact same formula the asserted net and the prompt ledger use, so the table,
// the Overview totals, and ledgerSummary.ts can never drift. The NET position
// follows verify-or-suppress (gated until cash categories are confirmed); the
// per-type subtotals are shown always, with each contributing line traceable
// and unconfirmed (AI-guess) lines counted.
//
// Pure function, no React, no AI — unit-tested via node --test.

import type { RecordFact } from "../types";
import {
  BUCKET_LABELS,
  bucketSubtotalCents,
  type LedgerBucket,
  type LedgerItem,
} from "./financialTotals";
import type { LedgerResult } from "./ledger";

export interface ContextGroup {
  bucket: LedgerBucket;
  label: string;
  // Subtotal honoring exclusions — bucketSubtotalCents, the shared formula.
  cents: number;
  // The participating contributing facts, so the subtotal is always traceable.
  items: LedgerItem[];
  count: number;
  // How many contributing lines are still AI-guesses (no user override).
  unconfirmedCount: number;
  // cost/recovery feed the net; coverage_term/rate are reference only.
  inNet: boolean;
}

export type ContextExcludedKind =
  | "subsumed" // fold component of a same-record cash total
  | "user" // user marked "already counted"
  | "unverified" // quote didn't verify
  | "no_parseable_value"; // no usable number

export interface ContextExcluded {
  fact: RecordFact;
  key: string;
  // The amount's value when known (subsumed / user-excluded); null for
  // unverified / unparseable amounts that never had a usable number.
  cents: number | null;
  kind: ContextExcludedKind;
  // For a subsumed component: the cash total it rolls into (for "part of $X").
  partOfCents: number | null;
}

export interface CostsByContext {
  groups: ContextGroup[];
  // Every amount kept OUT of the subtotals, with its reason — complete, so a
  // dashboard view is self-contained. Reversible exclusions (subsumed / user)
  // and unfixable ones (unverified / unparseable) are distinguished by `kind`.
  excluded: ContextExcluded[];
  // Net case position — Cost incurred − Recovery / received ONLY. Present only
  // when the verify-or-suppress gate is satisfied; null while any cash category
  // is unconfirmed (never assert a possibly-wrong net).
  net: { costCents: number; recoveryCents: number; netCents: number } | null;
  // When net is null: why (the confirm prompt). Null when net is present.
  netReason: string | null;
}

// Routes through the SHARED ledger so the cost/recovery groups are the SAME
// cross-record-merged, status-filtered transactions as the net (no within-
// record/status-ignorant divergence). cost = Paid transactions, recovery =
// Received; coverage_term/rate stay reference subtotals over the deduped items.
export function buildCostsByContext(ledger: LedgerResult): CostsByContext {
  // Money groups: one displayed line per transaction (representative item),
  // subtotal = the net's value, so the table can't double-count.
  const moneyGroup = (
    bucket: "cost" | "recovery",
    status: "Paid" | "Received",
    cents: number,
  ): ContextGroup => {
    const txns = ledger.transactions.filter((t) => t.status === status);
    return {
      bucket,
      label: BUCKET_LABELS[bucket],
      cents,
      items: txns.map((t) => t.items[0]!),
      count: txns.length,
      unconfirmedCount: 0, // a Paid/Received transaction is, by definition, triaged
      inNet: true,
    };
  };
  // Reference groups: coverage_term / rate, within-record deduped items.
  const refGroup = (bucket: LedgerBucket): ContextGroup => {
    const items = ledger.items.filter(
      (i) => i.bucket === bucket && !i.excluded && !i.subsumed,
    );
    return {
      bucket,
      label: BUCKET_LABELS[bucket],
      cents: bucketSubtotalCents(ledger.items, bucket),
      items,
      count: items.length,
      unconfirmedCount: items.filter((i) => !i.confirmed).length,
      inNet: false,
    };
  };

  const groups: ContextGroup[] = [
    moneyGroup("cost", "Paid", ledger.net.costCents),
    moneyGroup("recovery", "Received", ledger.net.recoveryCents),
    refGroup("coverage_term"),
    refGroup("rate"),
  ];

  const excluded: ContextExcluded[] = [
    ...ledger.items
      .filter((i) => i.subsumed)
      .map((i) => ({
        fact: i.fact,
        key: i.key,
        cents: i.cents,
        kind: "subsumed" as const,
        partOfCents: i.subsumedByCents,
      })),
    ...ledger.items
      .filter((i) => i.excluded)
      .map((i) => ({
        fact: i.fact,
        key: i.key,
        cents: i.cents,
        kind: "user" as const,
        partOfCents: null,
      })),
    ...ledger.excluded.map((x) => ({
      fact: x.fact,
      key: x.fact.id,
      cents: null,
      kind: x.reason,
      partOfCents: null,
    })),
  ];

  return {
    groups,
    excluded,
    net: ledger.net.asserted
      ? {
          costCents: ledger.net.costCents,
          recoveryCents: ledger.net.recoveryCents,
          netCents: ledger.net.netCents,
        }
      : null,
    netReason: ledger.net.asserted ? null : ledger.net.reason,
  };
}
